import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Grid,
    Alert,
    Snackbar,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Button,
    Collapse,
    Divider
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { differenceInMinutes } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Configureer moment voor tijdzone handling
moment.locale('nl');
moment.tz.setDefault('Europe/Amsterdam');

// Basis chart opties buiten de component
const getBaseOptions = (t) => ({
    animation: false,
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'cross'
        },
        formatter: function(params) {
            const time = moment(params[0].value[0]).format('DD MMMM YYYY HH:mm');
            let result = `${time}<br/>`;
            params.forEach(param => {
                if (param.seriesName === t('chart.autopilotOn')) {
                    result += `${param.seriesName}: ${param.value[1] === 1 ? 'Aan' : 'Uit'}<br/>`;
                } else if (param.seriesName === t('chart.price')) {
                    const price = Number(param.value[1]);
                    result += `${param.seriesName}: ${!isNaN(price) ? price.toFixed(4) : '0.0000'} €/kWh<br/>`;
                } else if (param.seriesName === t('chart.emissionFactor')) {
                    const emission = Number(param.value[1]);
                    result += `${param.seriesName}: ${!isNaN(emission) ? emission.toFixed(0) : '0'} g CO₂/kWh<br/>`;
                } else if (param.seriesName === t('chart.priceThreshold')) {
                    const threshold = Number(param.value[1]);
                    result += `${param.seriesName}: ${!isNaN(threshold) ? threshold.toFixed(4) : '0.0000'} €/kWh (maximale prijs voor aanzetten - aangepast voor weergave)<br/>`;
                } else if (param.seriesName === t('chart.maxPrice')) {
                    const maxPrice = Number(param.value[1]);
                    result += `${param.seriesName}: ${!isNaN(maxPrice) ? maxPrice.toFixed(4) : '0.0000'} €/kWh (absolute maximale prijs - aangepast voor weergave)<br/>`;
                } else if (param.seriesName === 'Waterstofprijs') {
                    const hydrogenPrice = Number(param.value[1]);
                    result += `${param.seriesName}: ${!isNaN(hydrogenPrice) ? hydrogenPrice.toFixed(2) : '0.00'} €/kg<br/>`;
                }
            });
            return result;
        }
    },
    legend: {
        data: [
            t('chart.autopilotOn'),
            t('chart.price'),
            t('chart.emissionFactor'),
            'Waterstofprijs',
            t('chart.priceThreshold'),
            t('chart.maxPrice')
        ],
        selected: {
            [t('chart.emissionFactor')]: false,
            'Waterstofprijs': false
        }
    },
    grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
    },
    xAxis: {
        type: 'time',
        min: moment().startOf('day').valueOf(),
        max: moment().endOf('day').valueOf(),
        axisLabel: {
            formatter: (value) => moment(value).format('HH:mm')
        }
    },
    yAxis: [
        {
            type: 'value',
            name: 'Stroomprijs (€/kWh)',
            scale: true,
            axisLabel: {
                formatter: '{value} €/kWh'
            }
        },
        {
            type: 'value',
            name: 'Status',
            min: 0,
            max: 1,
            show: false
        },
        {
            type: 'value',
            name: 'Emissiefactor (g CO₂/kWh)',
            scale: true,
            show: true,
            position: 'right',
            axisLabel: {
                formatter: '{value} g'
            }
        },
        {
            type: 'value',
            name: 'Waterstofprijs (€/kg)',
            min: 0,
            show: true,
            position: 'right',
            offset: 80,
            axisLabel: {
                formatter: '{value} €/kg'
            },
            splitLine: {
                show: false
            }
        }
    ]
});

// ChartComponent met ECharts
const ChartComponent = React.memo(({ scheduleData, settings, adjustedParameters, showEmissionFactor, setShowEmissionFactor, showHydrogenPrice, setShowHydrogenPrice, electricityPriceLabel, hydrogenPriceLabel }) => {
    const { t } = useTranslation();
    const chartRef = useRef(null);

    // Update de chart data wanneer scheduleData of settings verandert
    useEffect(() => {
        console.log('ChartComponent - adjustedParameters:', adjustedParameters);
        console.log('ChartComponent - scheduleData length:', scheduleData.length);
        if (!chartRef.current) return;

        const times = scheduleData.map(d => parseISO(d.validfrom).getTime());
        const prices = scheduleData.map(d => d.price);
        const emissionFactors = scheduleData.map(d => d.emissionfactor);
        const hydrogenPrices = scheduleData.map(d => d.hydrogen_price || null);
        const autopilotStatus = scheduleData.map(d => d.manual === null && d.automatic ? 1 : 0);

        const series = [
            {
                name: t('chart.autopilotOn'),
                type: 'bar',
                yAxisIndex: 1,
                data: times.map((time, index) => [time, autopilotStatus[index]]),
                itemStyle: {
                    color: 'rgba(54, 162, 235, 0.5)',
                    borderWidth: 0.2,
                    borderColor: 'rgba(54, 162, 235, 0.8)'
                },
                barWidth: '100%',
                barGap: '0%',
                barCategoryGap: '0%',
                emphasis: {
                    itemStyle: {
                        borderWidth: 0.2,
                        borderColor: 'rgba(54, 162, 235, 1)'
                    }
                }
            },
            {
                name: electricityPriceLabel || t('chart.price'),
                type: 'line',
                yAxisIndex: 0,
                data: times.map((time, index) => [time, prices[index]]),
                itemStyle: {
                    color: 'rgb(75, 192, 192)'
                },
                smooth: false,
                symbol: 'circle',
                symbolSize: 6,
                lineStyle: {
                    width: 2
                },
                emphasis: {
                    itemStyle: {
                        borderWidth: 2,
                        borderColor: 'rgb(75, 192, 192)',
                        color: 'transparent'
                    }
                }
            },
            {
                name: t('chart.emissionFactor'),
                type: 'line',
                yAxisIndex: 2,
                data: times.map((time, index) => [time, emissionFactors[index]]),
                itemStyle: {
                    color: 'rgb(255, 99, 132)'
                },
                smooth: false,
                symbol: 'none',
                lineStyle: {
                    width: 2
                }
            },
            {
                name: hydrogenPriceLabel || 'Waterstofprijs',
                type: 'line',
                yAxisIndex: 3,
                data: times.map((time, index) => [time, hydrogenPrices[index]]).filter(([time, price]) => price !== null),
                itemStyle: {
                    color: 'rgb(76, 175, 80)'
                },
                smooth: false,
                symbol: 'none',
                lineStyle: {
                    width: 2
                }
            },
            {
                name: t('chart.priceThreshold'),
                type: 'line',
                yAxisIndex: 0,
                data: times.length > 0 ? [
                    [times[0], adjustedParameters.price_threshold],
                    [times[times.length - 1], adjustedParameters.price_threshold]
                ] : [],
                itemStyle: {
                    color: 'rgb(156, 39, 176)'
                },
                lineStyle: {
                    type: 'dashed',
                    width: 2
                },
                symbol: 'none',
                z: 10
            },
            {
                name: t('chart.maxPrice'),
                type: 'line',
                yAxisIndex: 0,
                data: times.length > 0 ? [
                    [times[0], adjustedParameters.max_price],
                    [times[times.length - 1], adjustedParameters.max_price]
                ] : [],
                itemStyle: {
                    color: 'rgb(33, 150, 243)'
                },
                lineStyle: {
                    type: 'dashed',
                    width: 2
                },
                symbol: 'none',
                z: 10
            }
        ];

        const echartsInstance = chartRef.current.getEchartsInstance();
        echartsInstance.setOption({
            series: series,
            legend: {
                data: [
                    t('chart.autopilotOn'),
                    electricityPriceLabel || t('chart.price'),
                    t('chart.emissionFactor'),
                    hydrogenPriceLabel || 'Waterstofprijs',
                    t('chart.priceThreshold'),
                    t('chart.maxPrice')
                ],
                selected: {
                    [t('chart.emissionFactor')]: showEmissionFactor,
                    [hydrogenPriceLabel || 'Waterstofprijs']: showHydrogenPrice
                }
            },
            yAxis: [
                {
                    type: 'value',
                    name: electricityPriceLabel || 'Stroomprijs (€/kWh)',
                    scale: true,
                    axisLabel: {
                        formatter: '{value} €/kWh'
                    }
                },
                {
                    type: 'value',
                    name: 'Status',
                    min: 0,
                    max: 1,
                    show: false
                },
                {
                    type: 'value',
                    name: 'Emissiefactor (g CO₂/kWh)',
                    scale: true,
                    show: showEmissionFactor,
                    position: 'right',
                    axisLabel: {
                        formatter: '{value} g'
                    }
                },
                {
                    type: 'value',
                    name: 'Waterstofprijs (€/kg)',
                    min: 0,
                    show: showHydrogenPrice,
                    position: 'right',
                    offset: 80,
                    axisLabel: {
                        formatter: '{value} €/kg'
                    },
                    splitLine: {
                        show: false
                    }
                }
            ]
        });
    }, [scheduleData, settings, adjustedParameters, t, showEmissionFactor, showHydrogenPrice, electricityPriceLabel, hydrogenPriceLabel]);

    return (
        <Box height={500}>
            <ReactECharts
                ref={chartRef}
                option={getBaseOptions(t)}
                style={{ height: '100%', width: '100%' }}
                onEvents={{
                    legendselectchanged: (params) => {
                        if (params.name === t('chart.emissionFactor')) {
                            setShowEmissionFactor(params.selected[t('chart.emissionFactor')]);
                        } else if (params.name === (hydrogenPriceLabel || 'Waterstofprijs')) {
                            setShowHydrogenPrice(params.selected[hydrogenPriceLabel || 'Waterstofprijs']);
                        }
                        // Note: electricity price line visibility wordt automatisch gehandeld door ECharts
                    }
                }}
                opts={{ renderer: 'canvas' }}
            />
        </Box>
    );
}, (prevProps, nextProps) => {
    // Custom comparison functie voor React.memo
    return (
        prevProps.showEmissionFactor === nextProps.showEmissionFactor &&
        prevProps.showHydrogenPrice === nextProps.showHydrogenPrice &&
        prevProps.electricityPriceLabel === nextProps.electricityPriceLabel &&
        prevProps.hydrogenPriceLabel === nextProps.hydrogenPriceLabel &&
        prevProps.adjustedParameters.price_threshold === nextProps.adjustedParameters.price_threshold &&
        prevProps.adjustedParameters.max_price === nextProps.adjustedParameters.max_price &&
        prevProps.scheduleData.length === nextProps.scheduleData.length &&
        JSON.stringify(prevProps.scheduleData) === JSON.stringify(nextProps.scheduleData)
    );
});

// Parameter Field Component
const ParameterField = React.memo(({ 
    label, 
    value, 
    onChange, 
    type = 'text',
    min,
    max,
    step,
    info
}) => {
    const { t } = useTranslation();
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
                fullWidth
                label={t(label)}
                type={type}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                InputProps={{
                    inputProps: { min, max, step }
                }}
            />
            {info && (
                <Tooltip title={info} placement="right">
                    <IconButton size="small">
                        <InfoIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
});

// SaveButton Component
const SaveButton = React.memo(({ onSave, loading, hasChanges = false }) => {
    const { t } = useTranslation();
    
    return (
        <Tooltip title="Deze instellingen worden gebruikt voor het maken van toekomstige planningen">
            <span>
                <Button
                    variant="contained"
                    color={hasChanges ? "secondary" : "primary"}
                    startIcon={<SaveIcon />}
                    onClick={onSave}
                    disabled={loading || !hasChanges}
                    sx={{ mt: 2 }}
                >
                    {loading ? t('common.saving') : hasChanges ? t('common.save') : t('common.saved')}
                </Button>
            </span>
        </Tooltip>
    );
});

// Parameters Card Component
const ParametersCard = React.memo(({ settings, onSettingChange, onSave, saving, changedParameters, electricitySettings }) => {
    const { t } = useTranslation();
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Check welke parameters in deze card zijn gewijzigd
    const parametersInCard = ['price_threshold', 'max_price', 'min_run_time', 'ewma_span', 'window_size'];
    const hasChanges = parametersInCard.some(param => changedParameters.has(param));
    
    return (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                {t('simulation.parameters')}
            </Typography>
            {electricitySettings.show_all_in && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>📊 All-in prijsweergave actief</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Invoer:</strong> U vult parameters in op basis van kale stroomprijs<br/>
                        • <strong>Database:</strong> Parameters worden bewaard in kale stroomprijs<br/>
                        • <strong>Grafiek:</strong> Parameters worden automatisch verhoogd met €{((electricitySettings.netbeheerder_tarief || 0.12) + (electricitySettings.leverancier_tarief || 0.08)).toFixed(3)}/kWh voor consistente weergave
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        Voorbeeld: Invoer 0.05 €/kWh → Database 0.05 €/kWh → Grafiek toont {(0.05 + (electricitySettings.netbeheerder_tarief || 0.12) + (electricitySettings.leverancier_tarief || 0.08)).toFixed(3)} €/kWh
                    </Typography>
                </Alert>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                    <ParameterField
                        label="simulation.priceThresholdValue"
                        value={settings.price_threshold}
                        onChange={(value) => onSettingChange('price_threshold', value)}
                        type="number"
                        min={-999}
                        step={0.01}
                        info="De maximale prijs in €/kWh (kale stroomprijs) waarbij de elektrolyzer nog aan gaat. Als de gemiddelde prijs over het window_size aantal periodes onder deze drempel ligt, wordt de elektrolyzer ingeschakeld. Let op: dit is altijd de kale stroomprijs, ongeacht de weergave-instelling. Negatieve prijzen zijn toegestaan."
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                    <ParameterField
                        label="simulation.maxPriceValue"
                        value={settings.max_price}
                        onChange={(value) => onSettingChange('max_price', value)}
                        type="number"
                        min={-999}
                        step={0.01}
                        info="De absolute maximale prijs in €/kWh (kale stroomprijs) waarbij de elektrolyzer nog mag draaien. Als de prijs boven deze waarde komt, wordt de elektrolyzer direct uitgeschakeld, ongeacht andere instellingen. Let op: dit is altijd de kale stroomprijs, ongeacht de weergave-instelling. Negatieve prijzen zijn toegestaan."
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mt: 1 }}
                    >
                        {showAdvanced ? t('common.hideAdvanced') : t('common.showAdvanced')}
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Collapse in={showAdvanced}>
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle1" gutterBottom>
                                {t('simulation.advancedParameters')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <ParameterField
                                        label="simulation.minRuntime"
                                        value={settings.min_run_time}
                                        onChange={(value) => onSettingChange('min_run_time', value)}
                                        type="number"
                                        min={1}
                                        info="De minimale draaitijd in minuten voor de elektrolyzer. Deze tijd moet minimaal de ramp-up tijd (opstarten), ramp-down tijd (afschakelen) en een effectieve productietijd bevatten. Als een aan-periode korter zou zijn dan deze waarde, wordt deze automatisch uitgeschakeld om inefficiëntie te voorkomen."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <ParameterField
                                        label="simulation.ewmaSpan"
                                        value={settings.ewma_span}
                                        onChange={(value) => onSettingChange('ewma_span', value)}
                                        type="number"
                                        min={1}
                                        info="Het aantal periodes dat wordt gebruikt voor de Exponentially Weighted Moving Average (EWMA) berekening. Een hogere waarde zorgt voor een vloeiendere lijn en minder gevoeligheid voor pieken."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <ParameterField
                                        label="simulation.windowSize"
                                        value={settings.window_size}
                                        onChange={(value) => onSettingChange('window_size', value)}
                                        type="number"
                                        min={1}
                                        info="Het aantal periodes dat wordt gebruikt om te bepalen of de elektrolyzer aan gaat. Het systeem kijkt naar het gemiddelde van deze periodes om te beslissen of de prijs gunstig genoeg is."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </Grid>

                <Grid item xs={12}>
                    <SaveButton onSave={onSave} loading={saving} hasChanges={hasChanges} />
                </Grid>
            </Grid>
        </Paper>
    );
});

// Apparaat Parameters Card Component
const DeviceParametersCard = React.memo(({ settings, onSettingChange, onSave, saving, changedParameters }) => {
    const { t } = useTranslation();
    
    // Check welke parameters in deze card zijn gewijzigd
    const deviceParameters = ['power', 'efficiency'];
    const hasChanges = deviceParameters.some(param => changedParameters.has(param));
    
    return (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                {t('simulation.deviceParameters')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                    <ParameterField
                        label="simulation.power"
                        value={settings.power}
                        onChange={(value) => onSettingChange('power', value)}
                        type="number"
                        min={0}
                        step={0.1}
                        info="Het elektrisch vermogen van de elektrolyzer in kW. Dit wordt gebruikt om de waterstofproductie te berekenen."
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                    <ParameterField
                        label="simulation.efficiency"
                        value={settings.efficiency}
                        onChange={(value) => onSettingChange('efficiency', value)}
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        info="De efficiëntie van de elektrolyzer in procenten. Dit bepaalt hoeveel van het elektrisch vermogen daadwerkelijk wordt omgezet in waterstof."
                    />
                </Grid>
                <Grid item xs={12}>
                    <SaveButton onSave={onSave} loading={saving} hasChanges={hasChanges} />
                </Grid>
            </Grid>
        </Paper>
    );
});

// Loading Component met debounce
const LoadingOverlay = React.memo(({ loading }) => {
    const [showLoader, setShowLoader] = useState(false);
    
    useEffect(() => {
        let timeoutId;
        if (loading) {
            // Toon de loader alleen na 3 seconden
            timeoutId = setTimeout(() => {
                setShowLoader(true);
            }, 3000);
        } else {
            // Verberg de loader direct
            setShowLoader(false);
        }
        
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [loading]);
    
    if (!showLoader) return null;
    
    return (
        <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="400px"
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1000
            }}
        >
            <CircularProgress />
        </Box>
    );
});

const SimulationPage = () => {
    const { t } = useTranslation();
    const { get, post } = useApi();
    const translate = useTranslate();
    const [settings, setSettings] = useState({});
    const [originalSettings, setOriginalSettings] = useState({}); // Track originele waarden
    const [changedParameters, setChangedParameters] = useState(new Set()); // Track gewijzigde parameters
    const [initialLoad, setInitialLoad] = useState(true);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [scheduleData, setScheduleData] = useState([]);
    const chartRef = useRef(null);
    const [showEmissionFactor, setShowEmissionFactor] = useState(false);
    const [showHydrogenPrice, setShowHydrogenPrice] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deviceSaving, setDeviceSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [electricitySettings, setElectricitySettings] = useState({ show_all_in: true });
    const [hydrogenDisplaySettings, setHydrogenDisplaySettings] = useState({ hydrogen_type: 'green', show_retail: true });

    // Combineer loading states
    useEffect(() => {
        setIsLoading(initialLoad || dataLoading);
    }, [initialLoad, dataLoading]);

    // Haal instellingen op bij het laden van de pagina
    useEffect(() => {
        fetchSettings();
        fetchElectricitySettings();
        fetchHydrogenDisplaySettings();
    }, []);

    // Update de grafiek wanneer instellingen of planning data verandert
    useEffect(() => {
        if (settings && Object.keys(settings).length > 0) {
            fetchSimulationData();
        }
    }, [settings]);

    const fetchSettings = async () => {
        try {
            const response = await get('/config');
            if (response.data && response.data.configuration) {
                const settingsObject = {};
                response.data.configuration.forEach(setting => {
                    settingsObject[setting.parameter] = setting.value;
                });
                setSettings(settingsObject);
                setOriginalSettings(settingsObject); // Sla originele waarden op
                setChangedParameters(new Set()); // Reset gewijzigde parameters
            }
            setInitialLoad(false);
        } catch (error) {
            setError(t('errors.fetchSettings'));
            setInitialLoad(false);
        }
    };

    const fetchSimulationData = async () => {
        try {
            setDataLoading(true);
            const queryParams = new URLSearchParams({
                date: moment().format('YYYY-MM-DD'),
                ...settings
            }).toString();
            
            const response = await get(`/dryrun?${queryParams}`);
            if (response && response.data && response.data.blocks) {
                setScheduleData(response.data.blocks);
            }
        } catch (err) {
            console.error('Error fetching simulation data:', err);
            setError(t('errors.fetchSimulationFailed'));
        } finally {
            setDataLoading(false);
        }
    };

    const handleSettingChange = (parameter, value) => {
        const newValue = isNaN(value) ? 0 : value;
        setSettings(prev => ({
            ...prev,
            [parameter]: newValue
        }));
        
        // Check of de waarde is gewijzigd ten opzichte van de originele waarde
        const originalValue = originalSettings[parameter];
        if (originalValue !== undefined && parseFloat(originalValue) !== parseFloat(newValue)) {
            setChangedParameters(prev => new Set([...prev, parameter]));
        } else {
            setChangedParameters(prev => {
                const newSet = new Set(prev);
                newSet.delete(parameter);
                return newSet;
            });
        }
    };

    const handleEmissionFactorChange = useCallback((isVisible) => {
        console.log('Emission factor visibility changed:', isVisible);
        setShowEmissionFactor(isVisible);
    }, []);

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            
            // Alleen gewijzigde parameters opslaan
            const parametersToSave = ['price_threshold', 'max_price', 'min_run_time', 'ewma_span', 'window_size'];
            const changedParams = parametersToSave.filter(param => changedParameters.has(param));
            
            if (changedParams.length === 0) {
                console.log('Geen parameters gewijzigd, niets op te slaan');
                return;
            }
            
            const updates = changedParams.map(parameter => ({
                parameter,
                value: parseFloat(settings[parameter])
            }));

            console.log('Opslaan van gewijzigde parameters:', changedParams);

            for (const update of updates) {
                await post('/config', update);
            }

            // Update originele settings na succesvol opslaan
            setOriginalSettings(prev => {
                const newOriginal = { ...prev };
                changedParams.forEach(param => {
                    newOriginal[param] = settings[param];
                });
                return newOriginal;
            });
            
            // Reset gewijzigde parameters
            setChangedParameters(prev => {
                const newSet = new Set(prev);
                changedParams.forEach(param => newSet.delete(param));
                return newSet;
            });

            // Voeg een reschedule toe na het opslaan van de instellingen
            // Gebruik moment.js voor consistente tijdzone handling
            const now = moment().tz('Europe/Amsterdam');
            await post('/schedule/recalculate', {
                timestamp: now.utc().format('YYYY-MM-DDTHH:mm:ss')
            });

            // Sla waterstof prijzen op in database (omdat planning wijzigt)
            try {
                await post('/hydrogen-price/store-price-data', {
                    hours_forward: 24 // Sla komende 24 uur op (korter voor simulation)
                });
                console.log('Waterstof prijzen opgeslagen in database vanaf nu vooruit na planning wijzigingen');
            } catch (storeError) {
                console.warn('Kon waterstof prijzen niet opslaan in database:', storeError);
            }

            // Ververs de data na het opslaan
            await fetchSimulationData();

            setError(null);
            setSuccessMessage('Instellingen succesvol opgeslagen');
        } catch (err) {
            console.error('Error saving settings:', err);
            setError(t('errors.saveSettingsFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDeviceSettings = async () => {
        try {
            setDeviceSaving(true);
            
            // Alleen gewijzigde device parameters opslaan
            const deviceParameters = ['power', 'efficiency'];
            const changedDeviceParams = deviceParameters.filter(param => changedParameters.has(param));
            
            if (changedDeviceParams.length === 0) {
                console.log('Geen device parameters gewijzigd, niets op te slaan');
                return;
            }
            
            const updates = changedDeviceParams.map(parameter => ({
                parameter,
                value: parseFloat(settings[parameter])
            }));

            console.log('Opslaan van gewijzigde device parameters:', changedDeviceParams);

            for (const update of updates) {
                await post('/config', update);
            }

            // Update originele settings na succesvol opslaan
            setOriginalSettings(prev => {
                const newOriginal = { ...prev };
                changedDeviceParams.forEach(param => {
                    newOriginal[param] = settings[param];
                });
                return newOriginal;
            });
            
            // Reset gewijzigde parameters
            setChangedParameters(prev => {
                const newSet = new Set(prev);
                changedDeviceParams.forEach(param => newSet.delete(param));
                return newSet;
            });

            setError(null);
            setSuccessMessage('Apparaat instellingen succesvol opgeslagen');
        } catch (err) {
            console.error('Error saving device settings:', err);
            setError(t('errors.saveSettingsFailed'));
        } finally {
            setDeviceSaving(false);
        }
    };

    // Functie om de gemiddelde prijs, emissiefactor en waterstofprijs voor een periode te berekenen
    const calculateAverages = (start, end) => {
        const relevantData = scheduleData.filter(data => {
            const time = parseISO(data.validfrom).getTime();
            return time >= start && time < end;
        });

        if (relevantData.length === 0) return { price: 0, emissionFactor: 0, hydrogenPrice: 0 };

        const totalPrice = relevantData.reduce((sum, data) => sum + data.price, 0);
        const totalEmissionFactor = relevantData.reduce((sum, data) => sum + data.emissionfactor, 0);
        
        // Filter alleen data met waterstofprijs voor gemiddelde berekening
        const hydrogenData = relevantData.filter(data => 
            data.hydrogen_price !== null && 
            data.hydrogen_price !== undefined
        );
        const totalHydrogenPrice = hydrogenData.reduce((sum, data) => sum + data.hydrogen_price, 0);

        return {
            price: totalPrice / relevantData.length,
            emissionFactor: totalEmissionFactor / relevantData.length,
            hydrogenPrice: hydrogenData.length > 0 ? totalHydrogenPrice / hydrogenData.length : 0
        };
    };

    // Bereken actieve periodes en totalen
    const calculateActivePeriods = () => {
        if (!scheduleData || !Array.isArray(scheduleData)) return [];
        
        // Sorteer de blokken op starttijd
        const sortedData = [...scheduleData].sort(
            (a, b) => parseISO(a.validfrom).getTime() - parseISO(b.validfrom).getTime()
        );

        const periods = [];
        let currentPeriod = null;

        sortedData.forEach((block) => {
            const isOn = block.manual === true || (block.manual === null && block.automatic);
            const status = block.manual === true ? t('periods.statusOverride') : t('periods.statusAutopilot');

            if (isOn) {
                if (!currentPeriod) {
                    // Start nieuwe periode
                    currentPeriod = {
                        start: parseISO(block.validfrom).getTime(),
                        end: parseISO(block.validto).getTime(),
                        status: status
                    };
                } else if (currentPeriod.status === status) {
                    // Verleng bestaande periode
                    currentPeriod.end = parseISO(block.validto).getTime();
                } else {
                    // Einde van periode, start nieuwe
                    periods.push(currentPeriod);
                    currentPeriod = {
                        start: parseISO(block.validfrom).getTime(),
                        end: parseISO(block.validto).getTime(),
                        status: status
                    };
                }
            } else if (currentPeriod) {
                // Einde van periode
                periods.push(currentPeriod);
                currentPeriod = null;
            }
        });

        // Voeg laatste periode toe als die er is
        if (currentPeriod) {
            periods.push(currentPeriod);
        }

        // Bereken verbruik en kosten voor elke periode
        return periods.map(period => {
            const duration = differenceInMinutes(period.end, period.start);
            const hours = duration / 60; // Omrekenen naar uren voor berekening
            const power = parseFloat(settings.power) || 0; // kW
            const averages = calculateAverages(period.start, period.end);
            
            // Waterstof berekeningen
            const totalConsumption = power * hours; // kWh
            const efficiency = parseFloat(settings.efficiency) / 100 || 0; // Efficiëntie als decimaal
            const energyForHydrogen = totalConsumption * efficiency; // kWh effectief gebruikt voor waterstof
            const energyPerKgH2 = parseFloat(settings.energy_per_kg_h2) || 13.3333; // kWh per kg waterstof
            const hydrogenProduced = energyForHydrogen / energyPerKgH2; // kg waterstof
            
            return {
                ...period,
                duration,
                hours,
                power,
                totalConsumption,
                averagePrice: averages.price,
                averageEmissionFactor: averages.emissionFactor,
                averageHydrogenPrice: averages.hydrogenPrice,
                totalCost: totalConsumption * averages.price, // €
                hydrogenProduced,
                hydrogenCost: hydrogenProduced * averages.hydrogenPrice // € kosten voor waterstof
            };
        });
    };

    const calculateTotals = () => {
        if (!scheduleData || !Array.isArray(scheduleData)) {
            return {
                totalRuntime: '0 uur',
                totalConsumption: '0.00 kWh',
                averagePrice: '0.00 €/kWh',
                totalCost: '0.00 €',
                totalEmission: '0.00 kg CO₂',
                averageEmissionFactor: '0 g CO₂/kWh',
                totalHydrogen: '0.00 kg',
                emissionPerHydrogen: '0.00 kg CO₂/kg H₂'
            };
        }
        
        const periods = calculateActivePeriods();
        const totalMinutes = periods.reduce((sum, period) => sum + period.duration, 0);
        const totalConsumption = periods.reduce((sum, period) => sum + period.totalConsumption, 0);
        const totalCost = periods.reduce((sum, period) => sum + period.totalCost, 0);
        const totalEmission = periods.reduce((sum, period) => 
            sum + (period.totalConsumption * period.averageEmissionFactor), 0) / 1000; // kg CO₂
        const totalHydrogen = periods.reduce((sum, period) => sum + period.hydrogenProduced, 0);
        const totalHydrogenCost = periods.reduce((sum, period) => sum + period.hydrogenCost, 0);

        return {
            totalRuntime: `${Math.floor(totalMinutes / 60)} uur${totalMinutes % 60 > 0 ? ` en ${totalMinutes % 60} minuten` : ''}`,
            totalConsumption: `${totalConsumption.toFixed(1)} kWh`,
            averagePrice: `${(totalCost / totalConsumption).toFixed(4)} €/kWh`,
            totalCost: `${totalCost.toFixed(2)} €`,
            totalEmission: `${totalEmission.toFixed(2)} kg CO₂`,
            averageEmissionFactor: `${(totalEmission * 1000 / totalConsumption).toFixed(0)} g CO₂/kWh`,
            totalHydrogen: `${totalHydrogen.toFixed(2)} kg`,
            emissionPerHydrogen: `${(totalEmission / totalHydrogen).toFixed(2)} kg CO₂/kg H₂`,
            averageHydrogenPrice: `${totalHydrogen > 0 ? (totalHydrogenCost / totalHydrogen).toFixed(2) : '0.00'} €/kg`,
            totalHydrogenCost: `${totalHydrogenCost.toFixed(2)} €`
        };
    };

    // Functie om het juiste stroomprijs label te bepalen
    const getElectricityPriceLabel = () => {
        return electricitySettings.show_all_in 
            ? 'All-in Stroomprijs (€/kWh)'
            : 'Kale Stroomprijs (€/kWh)';
    };

    // Functie om het juiste waterstofprijs label te bepalen
    const getHydrogenPriceLabel = () => {
        const typeMapping = {
            'green': 'Groene',
            'grey': 'Grijze', 
            'blue': 'Blauwe'
        };
        const typeName = typeMapping[hydrogenDisplaySettings.hydrogen_type] || 'Groene';
        const priceType = hydrogenDisplaySettings.show_retail ? 'Retail' : 'Markt';
        return `${typeName} Waterstof ${priceType} Prijs (€/kg)`;
    };

    // Functie om parameters aan te passen op basis van electricity settings voor weergave
    const getAdjustedParametersForDisplay = () => {
        if (!electricitySettings.show_all_in) {
            // Toon kale prijs, parameters blijven zoals ze zijn
            return {
                price_threshold: settings.price_threshold,
                max_price: settings.max_price
            };
        } else {
            // Toon all-in prijs, reken parameters om naar all-in
            const netbeheerder_tarief = electricitySettings.netbeheerder_tarief || 0.12;
            const leverancier_tarief = electricitySettings.leverancier_tarief || 0.08;
            const totalAdditionalCost = netbeheerder_tarief + leverancier_tarief;
            
            const originalThreshold = parseFloat(settings.price_threshold) || 0;
            const originalMaxPrice = parseFloat(settings.max_price) || 0;
            
            const adjustedParams = {
                price_threshold: originalThreshold + totalAdditionalCost,
                max_price: originalMaxPrice + totalAdditionalCost
            };
            
            // Validatie om te zorgen dat we geldige nummers hebben
            if (isNaN(adjustedParams.price_threshold)) {
                adjustedParams.price_threshold = totalAdditionalCost;
            }
            if (isNaN(adjustedParams.max_price)) {
                adjustedParams.max_price = totalAdditionalCost;
            }
            
            console.log('=== ADJUSTED PARAMETERS DEBUG ===');
            console.log('Electricity settings:', electricitySettings);
            console.log('Original parameters:', { price_threshold: settings.price_threshold, max_price: settings.max_price });
            console.log('Parsed original:', { originalThreshold, originalMaxPrice });
            console.log('Additional costs:', { netbeheerder_tarief, leverancier_tarief, totalAdditionalCost });
            console.log('Final adjusted parameters:', adjustedParams);
            console.log('=== END DEBUG ===');
            
            return adjustedParams;
        }
    };

    const fetchElectricitySettings = async () => {
        try {
            const response = await get('/electricity-price/settings');
            if (response.data) {
                console.log('Electricity settings loaded:', response.data);
                setElectricitySettings(response.data);
            }
        } catch (err) {
            console.error('Fout bij ophalen electricity price settings:', err);
            // Gebruik default settings bij fout
            const defaultSettings = { 
                show_all_in: true, 
                netbeheerder_tarief: 0.12, 
                leverancier_tarief: 0.08 
            };
            console.log('Using default electricity settings:', defaultSettings);
            setElectricitySettings(defaultSettings);
        }
    };

    const fetchHydrogenDisplaySettings = async () => {
        try {
            const response = await get('/hydrogen-price/display-settings');
            if (response.data) {
                setHydrogenDisplaySettings(response.data);
            }
        } catch (err) {
            console.error('Fout bij ophalen hydrogen display settings:', err);
            // Gebruik default settings bij fout
            setHydrogenDisplaySettings({ hydrogen_type: 'green', show_retail: true });
        }
    };

    if (initialLoad) {
        return (
            <LoadingOverlay loading={initialLoad} />
        );
    }

    const activePeriods = calculateActivePeriods();
    const totals = calculateTotals();

    return (
        <Box sx={{ p: 3, position: 'relative' }}>
            <Typography variant="h4" gutterBottom>
                {t('simulation.title')}
            </Typography>
            <Typography variant="body1" paragraph>
                {t('simulation.description')}
            </Typography>

            <LoadingOverlay loading={isLoading} />

            {!initialLoad && (
                <>
                    <ParametersCard 
                        settings={settings}
                        onSettingChange={handleSettingChange}
                        onSave={handleSaveSettings}
                        saving={saving}
                        changedParameters={changedParameters}
                        electricitySettings={electricitySettings}
                    />

                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('chart.title')}
                        </Typography>
                        <ChartComponent 
                            scheduleData={scheduleData}
                            settings={settings}
                            adjustedParameters={getAdjustedParametersForDisplay()}
                            showEmissionFactor={showEmissionFactor}
                            setShowEmissionFactor={handleEmissionFactorChange}
                            showHydrogenPrice={showHydrogenPrice}
                            setShowHydrogenPrice={setShowHydrogenPrice}
                            electricityPriceLabel={getElectricityPriceLabel()}
                            hydrogenPriceLabel={getHydrogenPriceLabel()}
                        />
                    </Paper>

                    <DeviceParametersCard 
                        settings={settings}
                        onSettingChange={handleSettingChange}
                        onSave={handleSaveDeviceSettings}
                        saving={deviceSaving}
                        changedParameters={changedParameters}
                    />

                    {/* Aan-periodes */}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('periods.title')}
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('periods.status')}</TableCell>
                                        <TableCell>{t('periods.start')}</TableCell>
                                        <TableCell>{t('periods.end')}</TableCell>
                                        <TableCell>{t('periods.duration')}</TableCell>
                                        <TableCell align="right">{t('periods.consumption')}</TableCell>
                                        <TableCell align="right">{t('periods.averagePrice')}</TableCell>
                                        {showEmissionFactor && (
                                            <>
                                                <TableCell align="right">{t('periods.emissionFactor')}</TableCell>
                                                <TableCell align="right">{t('periods.emission')}</TableCell>
                                            </>
                                        )}
                                        <TableCell align="right">{t('periods.costs')}</TableCell>
                                        <TableCell align="right">{t('periods.hydrogen')}</TableCell>
                                        {showHydrogenPrice && (
                                            <>
                                                <TableCell align="right">H₂ prijs (€/kg)</TableCell>
                                                <TableCell align="right">H₂ waarde (€)</TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activePeriods.map((period, index) => {
                                        const hours = Math.floor(period.hours);
                                        const minutes = period.duration % 60;
                                        const durationText = `${hours} uur${minutes > 0 ? ` en ${minutes} minuten` : ''}`;
                                        const totalEmission = (period.totalConsumption * period.averageEmissionFactor) / 1000; // Omrekenen naar kg
                                        
                                        return (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Typography color={period.status === t('periods.statusOverride') ? 'success.main' : 'primary.main'}>
                                                        {period.status}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{moment(period.start).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                                <TableCell>{moment(period.end).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                                <TableCell>{durationText}</TableCell>
                                                <TableCell align="right">{period.totalConsumption.toFixed(1)}</TableCell>
                                                <TableCell align="right">{period.averagePrice.toFixed(4)}</TableCell>
                                                {showEmissionFactor && (
                                                    <>
                                                        <TableCell align="right">{period.averageEmissionFactor.toFixed(0)}</TableCell>
                                                        <TableCell align="right" sx={{ color: 'error.main' }}>{totalEmission.toFixed(2)}</TableCell>
                                                    </>
                                                )}
                                                <TableCell align="right">{period.totalCost.toFixed(2)}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                    {period.hydrogenProduced.toFixed(2)}
                                                </TableCell>
                                                {showHydrogenPrice && (
                                                    <>
                                                        <TableCell align="right" sx={{ color: 'success.main' }}>
                                                            {period.averageHydrogenPrice > 0 ? period.averageHydrogenPrice.toFixed(2) : '-'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                            {period.hydrogenCost > 0 ? period.hydrogenCost.toFixed(2) : '-'}
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Totalen */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('totals.title')}
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row">{t('totals.runtime')}</TableCell>
                                        <TableCell align="right">{totals.totalRuntime}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">{t('totals.consumption')}</TableCell>
                                        <TableCell align="right">{totals.totalConsumption}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">{t('totals.averagePrice')}</TableCell>
                                        <TableCell align="right">{totals.averagePrice}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">{t('totals.totalCosts')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.totalCost}</TableCell>
                                    </TableRow>
                                    {showEmissionFactor && (
                                        <>
                                            <TableRow>
                                                <TableCell component="th" scope="row">{t('totals.totalEmission')}</TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                                    {totals.totalEmission}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="th" scope="row">{t('totals.averageEmissionFactor')}</TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                                    {totals.averageEmissionFactor}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                    <TableRow>
                                        <TableCell component="th" scope="row">{t('totals.totalHydrogen')}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                            {totals.totalHydrogen}
                                        </TableCell>
                                    </TableRow>
                                    {showEmissionFactor && (
                                        <TableRow>
                                            <TableCell component="th" scope="row">{t('totals.emissionPerHydrogen')}</TableCell>
                                            <TableCell align="right" sx={{ color: 'error.main' }}>
                                                {totals.emissionPerHydrogen}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {showHydrogenPrice && (
                                        <>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Gemiddelde waterstofprijs</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                                    {totals.averageHydrogenPrice}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Totale waterstofwaarde</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                    {totals.totalHydrogenCost}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {/* Foutmelding */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={() => setError(null)}
            >
                <Alert onClose={() => setError(null)} severity="error">
                    {error}
                </Alert>
            </Snackbar>

            {/* Succesbericht */}
            <Snackbar 
                open={!!successMessage} 
                autoHideDuration={4000} 
                onClose={() => setSuccessMessage(null)}
            >
                <Alert onClose={() => setSuccessMessage(null)} severity="success">
                    {successMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SimulationPage; 